import moment from 'moment'

// 空白日オブジェクト
const BLANK_DAY = {
  displayDay: '',
  dayYyyyMmdd: '',
  display: false,
  daysOfTheWeek: '',
  weekNumber: '',
  pastDay: true
}

// 空白予約可能紹介枠オブジェクト
const BLANK_SLOT = {
  reception_hour: null,
  available_type: null
}

// 1週間のマス数
const WEEK_NUMBER = 7

// 予約可能紹介枠、1行あたりのマス数
const SLOT_ROW_NUMBER = 6

// 大カテゴリーID (カーライフスクエア)
const LARGE_CATEGORY_ID = 28

// 中カテゴリーID (コミっと車検事前見積)
const MIDDLE_CATEGORY_ID = 101

/**
* カレンダー描画用オブジェクト作成関数
* 「[S008] ネット予約受付日カレンダー情報返却API」のレスポンスの状態だと
* 描画が不可能な為、整形処理を行う。
* @param {string} calendarRow - [S008] API レスポンス結果
* @return {object} - カレンダー描画用オブジェクト、カレンダー描画用年月
*/
const shapedCalendar = (calendarRow) => {
  // Vueで描画しやすい用に整形処理を行う（カレンダー日付の配列も同時に取得）
  const { fixedCalendar, shapedYyyyMm } = fixCalendar(calendarRow)
  // 各月の1日以前に空配列を付け足す
  const addedFirstBlankCalendar = addCalendarBlankFirst(fixedCalendar)
  // 各月の最終日以降に空配列を付け足す
  const addedLastBlankCalendar = addCalendarBlankLast(addedFirstBlankCalendar)
  // 表示用に週毎に分割処理を行う
  const shapedCalendar = getSplitCalendar(addedLastBlankCalendar)
  return {
    shapedCalendar,
    shapedYyyyMm
  }
}

/**
* カレンダー基本整形処理
* @param {object} calendarRow - [S008] API レスポンス結果
* @return {object,string} - カレンダー基本オブジェクト、カレンダー描画用年月
*/
const fixCalendar = (calendarRow) => {
  let monthlyNumber = 0
  const fixedCalendar = []
  const shapedYyyyMm = []
  calendarRow.forEach(monthlyCalendar => {
    fixedCalendar[monthlyNumber] = []
    shapedYyyyMm.push(getDisplayYyyyMm(monthlyCalendar.year_month))
    monthlyCalendar.weeks.forEach(weeklyCalendar => {
      weeklyCalendar.days.forEach(daysCalendar => {
        fixedCalendar[monthlyNumber].push({
          displayDay: getDisplayDay(daysCalendar.date),
          dayYyyyMmdd: daysCalendar.date,
          display: Boolean(daysCalendar.available_flag === 1),
          daysOfTheWeek: daysCalendar.days_of_the_week_for_display,
          weekNumber: weeklyCalendar.week_no,
          pastDay: pastDayOrNot(daysCalendar.date)
        })
      })
    })
    monthlyNumber++
  })
  return {
    fixedCalendar,
    shapedYyyyMm
  }
}

/**
* カレンダー1日以前空白マス追加関数
* @param {object} calendar - カレンダー基本オブジェクト
* @return {object} - 先頭空白マス追加カレンダー基本オブジェクト
*/
const addCalendarBlankFirst = (calendar) => {
  for (let i = 0; i < calendar.length; i++) {
    const firstDay = calendar[i][0]
    const blankNumber = getCalendarBlankFirstNumber(firstDay.daysOfTheWeek)
    calendar[i] = addCalendarBlankFirstExec(calendar[i], blankNumber)
  }
  return calendar
}

/**
* 1日の曜日に対して何マス分追加すれば良いのか判定関数
* @param {string} daysOfTheWeek - 曜日文字列
* @return {number} - 追加必要マス
*/
const getCalendarBlankFirstNumber = (daysOfTheWeek) => {
  switch (daysOfTheWeek) {
    case '月':
      return 1
    case '火':
      return 2
    case '水':
      return 3
    case '木':
      return 4
    case '金':
      return 5
    case '土':
      return 6
    case '日':
      return 0
  }
}

/**
* カレンダー1日以前空白マス追加実行関数
* @param {array} monthCalendar - １ヶ月あたりのカレンダー
* @param {number} blankNumber - 追加マス
* @return {array} - 先頭空白マス追加１ヶ月あたりのカレンダー
*/
const addCalendarBlankFirstExec = (monthCalendar, blankNumber) => {
  if (blankNumber === 0) return monthCalendar
  for (let i = 0; i < blankNumber; i++) {
    monthCalendar.unshift(BLANK_DAY)
  }
  return monthCalendar
}

/**
* カレンダー最終日以降空白マス追加関数
* @param {object} calendar - カレンダー基本オブジェクト
* @return {object} - 末尾空白マス追加カレンダー基本オブジェクト
*/
const addCalendarBlankLast = (calendar) => {
  for (let i = 0; i < calendar.length; i++) {
    calendar[i] = addCalendarBlankLastExec(calendar[i], getCalendarMaxBox(calendar[i].length) - calendar[i].length)
  }
  return calendar
}

/**
* カレンダー最大マス数取得関数
* @param {number} calendarLength - 先頭空白マス追加カレンダー数
* @return {number} - 最大マス数
*/
const getCalendarMaxBox = (calendarLength) => {
  return (Math.floor(calendarLength / WEEK_NUMBER) + 1) * WEEK_NUMBER
}

/**
* カレンダー最終日以降空白マス追加実行関数
* @param {array} monthCalendar - １ヶ月あたりのカレンダー
* @param {number} blankNumber - 追加マス
* @return {array} - 末尾空白マス追加１ヶ月あたりのカレンダー
*/
const addCalendarBlankLastExec = (monthCalendar, blankNumber) => {
  for (let i = 0; i < blankNumber; i++) {
    monthCalendar.push(BLANK_DAY)
  }
  return monthCalendar
}

/**
* カレンダー描画用オブジェクト作成関数
* @param {object} calendars - 基本整形処理済みカレンダー
* @return {object} - カレンダー描画用オブジェクト
*/
const getSplitCalendar = (calendars) => {
  const splitedCalendar = []
  let calendarNumber = 0

  calendars.forEach(calendar => {
    splitedCalendar[calendarNumber] = []
    const loopCount = calendar.length / WEEK_NUMBER
    for (let i = 0; i < loopCount; i++) {
      splitedCalendar[calendarNumber].push({ row: [] })
      const splitNumber = i * WEEK_NUMBER
      const oneWeek = calendar.slice(splitNumber, splitNumber + WEEK_NUMBER)
      splitedCalendar[calendarNumber][i].row = oneWeek
    }
    calendarNumber++
  })

  return splitedCalendar
}

/**
* カレンダー日算出関数
* @param {string} stringDate - カレンダー年月日
* @return {string} -  カレンダー日
*/
const getDisplayDay = (stringDate) => {
  const date = new Date(stringDate)
  return date.getDate()
}

/**
* 描画用カレンダー年月作成関数
* @param {string} stringDate - カレンダー年月
* @return {string} -  描画用カレンダー年日
*/
const getDisplayYyyyMm = (stringYyyyMm) => {
  const date = new Date(`${stringYyyyMm}-01`)
  return `${date.getFullYear()}年${date.getMonth() + 1}月`
}

/**
* 予約可能紹介枠一覧描画用オブジェクト作成関数
* 「[S001] web予約可能枠照会API」のレスポンスの状態だと
* 描画が不可能な為、整形処理を行う。
* @param {object} SlotsRow - [S001] API レスポンス結果
* @param {object} dayInformation - 予約日情報
* @return {object,string} - 予約可能紹介枠一覧描画用オブジェクト、描画用年月日
*/
const shapedSlots = (SlotsRow, dayInformation) => {
  // 日別の配列で予約照会枠が渡ってくるため、先頭の配列を定数管理
  const targertSlot = SlotsRow[0]
  // 表示用のyyyy-mm-ddを取得
  const shapedYyyyMmDd = getDisplayYyyyMmDd(targertSlot.calendar_date, dayInformation.daysOfTheWeek)
  const slotLength = targertSlot.reception_hour_list.length
  const blankNumber = getSlotBlankNumber(slotLength)
  // 表示崩れ防止の為、最終予約枠以降に空配列を付け足す
  const addedBlankSlots = addSlotsBlankLastExec(targertSlot.reception_hour_list, blankNumber)
  // 表示用に5枠毎に分割処理を行い、行番号を付与させる
  const splitSlots = getSplitSlots(addedBlankSlots)
  const shapedSlots = addRownumberToSlots(splitSlots)
  return {
    shapedSlots,
    shapedYyyyMmDd
  }
}

/**
* 描画用年月日を取得する
* @param {string} stringYyyyMmDd - 年月日文字列
* @param {string} daysOfTheWeek - 曜日文字列
* @return {string} - 描画用年月日
*/
const getDisplayYyyyMmDd = (stringYyyyMmDd, daysOfTheWeek) => {
  return `${stringYyyyMmDd.substr(0, 4)}年${stringYyyyMmDd.substr(4, 2)}月${stringYyyyMmDd.substr(6, 2)}日（${daysOfTheWeek}）`
}

/**
* 予約可能紹介枠用の必要空白マス数を取得する
* @param {number} slotLength - 予約可能紹介枠マス数
* @return {number} - 必要空白マス数
*/
const getSlotBlankNumber = (slotLength) => {
  // 倍数判定、割り切れる場合は0返却
  if ((slotLength % SLOT_ROW_NUMBER) === 0) return 0
  return ((Math.floor(slotLength / SLOT_ROW_NUMBER) + 1) * SLOT_ROW_NUMBER) - slotLength
}

/**
* 予約可能紹介枠用末尾空白マス追加実行関数
* @param {array} SlotList - 予約可能紹介枠
* @param {number} blankNumber - 追加マス
* @return {array} - 末尾空白マス追加予約可能紹介枠
*/
const addSlotsBlankLastExec = (SlotList, blankNumber) => {
  for (let i = 0; i < blankNumber; i++) {
    SlotList.push(BLANK_SLOT)
  }
  return SlotList
}

/**
* 描画用予約可能紹介枠整形処理
* @param {array} addedBlankSlots - 空白マス追加予約可能紹介枠
* @return {array} - 描画用予約可能紹介枠
*/
const getSplitSlots = (addedBlankSlots) => {
  const slots = []
  const loopCount = addedBlankSlots.length / SLOT_ROW_NUMBER
  for (let i = 0; i < loopCount; i++) {
    slots.push({ row: [] })
    const splitNumber = i * SLOT_ROW_NUMBER
    const splitSlot = addedBlankSlots.slice(splitNumber, splitNumber + SLOT_ROW_NUMBER)
    slots[i].row = splitSlot
  }
  return slots
}

/**
* 描画用予約可能紹介枠行番号付与整形処理
* @param {array} splitSlots - 描画用予約可能紹介枠
* @return {array} - 描画用予約可能紹介枠
*/
const addRownumberToSlots = (splitSlots) => {
  let rowNumber = 0
  let columnNumber = 0
  splitSlots.forEach(rowSlots => {
    rowSlots.row.forEach(slot => {
      splitSlots[rowNumber].row[columnNumber].row_number = rowNumber
      columnNumber++
    })
    rowNumber++
    columnNumber = 0
  })
  return splitSlots
}

/**
* PITLOCKカレンダー利用可否判定関数
* [CLS003] 店舗のメニューリストAPIのレスポンス結果を判別して
* 利用可否であるか処理を行う
* @param {object} menuDetailList - [CLS003] API レスポンス結果
* @return {boolean} - 成功可否
*/
const getCanUsePitlock = (menuDetailList, userMenuType) => {
  const largeMenu = getLargeMenu(menuDetailList)
  const middleMenu = getMiddleMenu(largeMenu)
  const canUsePitlock = existCheckMenuType(middleMenu, userMenuType)
  return canUsePitlock
}

/**
* 大カテゴリー判定関数
* @param {object} menuDetailList - [CLS003] API レスポンス結果
* @return {array}
*/
const getLargeMenu = (menuDetailList) => {
  if (!menuDetailList.length) return false
  for (const largeMenu of menuDetailList) {
    if (largeMenu.large_category_id === LARGE_CATEGORY_ID) {
      return largeMenu
    }
  }
  return false
}

/**
* 中カテゴリー判定関数
* @param {object} largeMenu 大カテゴリーメニューオブジェクト
* @return {array}
*/
const getMiddleMenu = (largeMenu) => {
  if (!largeMenu) return false
  for (const middleMenu of largeMenu.middle_categories) {
    if (middleMenu.middle_category_id === MIDDLE_CATEGORY_ID) {
      return middleMenu
    }
  }
  return false
}

/**
* メニュータイプ存在チェック関数
* @param {object} middleMenu 中カテゴリーメニューオブジェクト
* @param {string} userMenuType ユーザー自動車種別
* @return {boolean}
*/
const existCheckMenuType = (middleMenu, userMenuType) => {
  if (!middleMenu) return false
  for (const menuType of middleMenu.menu_types) {
    if (menuType.menu_type === userMenuType) {
      return true
    }
  }
  return false
}

/**
* 過去日判定関数
*/
const pastDayOrNot = (yyyyMmDd) => {
  const today = moment().format('YYYY-MM-DD')
  if (yyyyMmDd < today) {
    return true
  }
  return false
}

// Vue側で利用する関数だけエクスポート。
export default {
  shapedCalendar,
  shapedSlots,
  getCanUsePitlock
}
